import { NotificationType } from "../ui/notification-handler";
import { Player } from "../models/player";
import { showBeamUpParticle } from "../ui/splash-particle";

export interface PlayerListenerOptions {
  otherPlayers: { [key: string]: Player };
  mySquadCode?: string;
  uiCamera?: Phaser.Cameras.Scene2D.Camera;
  adapterType: "instance" | "market";
  windowManager?: { showNotification: (message: string, type: NotificationType) => void };
}

/**
 * Register listeners for player position syncing and player join/leave events.
 * Supports both MainScene (instance-manager) and MarketScene (market-manager) events.
 *
 * instance mode: Listens to playerState, playerJoined, playerLeft
 * market mode: Listens to market-player-moved, market-player-entered, market-player-left
 */
export function registerPlayerListeners(
  scene: any,
  options: PlayerListenerOptions,
) {
  if (!window.socket) return;

  const { otherPlayers, mySquadCode, uiCamera, adapterType, windowManager } = options;

  // Unified handler for player joined event
  const handlePlayerJoined = (p: {
    id: string;
    x: number;
    y: number;
    weapon?: any;
    activeSkin?: string;
    equippedHat?: number;
    squadCode?: string;
    accountId?: number;
    accountName?: string;
    isReconnecting?: boolean;
  }) => {
    if (p.id === window.socket.id) {
      return; // Ignore self
    }

    // If this is a reconnecting player, find and remove their old sprite by accountId
    if (p.isReconnecting && p.accountId) {
      const socketIds = Object.keys(otherPlayers);
      for (let i = 0; i < socketIds.length; i++) {
        const oldSocketId = socketIds[i];
        const otherPlayer = otherPlayers[oldSocketId];
        if (
          (otherPlayer as any).accountId === p.accountId &&
          oldSocketId !== p.id
        ) {
          console.info(
            `[CLIENT] Removing old sprite for reconnecting player (old socket: ${oldSocketId}, new socket: ${p.id}, accountId: ${p.accountId})`,
          );
          otherPlayer.destroy();
          delete otherPlayers[oldSocketId];
        }
      }
    } else if (otherPlayers[p.id]) {
      // Remove if same socket ID already exists
      otherPlayers[p.id].destroy();
      delete otherPlayers[p.id];
    }

    // Create new player
    otherPlayers[p.id] = new Player(
      p.id,
      scene,
      p.x,
      p.y,
      100,
      100,
      true,
    );

    // Store squad code and accountId on player
    (otherPlayers[p.id] as any).squadCode = p.squadCode || null;
    (otherPlayers[p.id] as any).accountId = p.accountId || null;

    // Apply account name
    if (p.accountName) {
      otherPlayers[p.id].data.name = p.accountName;
      otherPlayers[p.id].healthbar?.setName(p.accountName);
    }

    // Set weapon equipment
    if (p.weapon) {
      if (!otherPlayers[p.id].data.equipment) {
        otherPlayers[p.id].data.equipment = {};
      }
      otherPlayers[p.id].data.equipment.Weapon = p.weapon;
      otherPlayers[p.id].updateWeaponSprite();
    }

    // Apply active skin
    if (p.activeSkin) {
      otherPlayers[p.id].data.activeSkin = p.activeSkin;
      otherPlayers[p.id].updateSkin(p.activeSkin);
    }

    // Apply equipped hat
    otherPlayers[p.id].data.equippedHat = p.equippedHat;
    otherPlayers[p.id].updateHatSprite();

    // Show squad indicator if in same squad
    if (p.squadCode && p.squadCode === mySquadCode) {
      otherPlayers[p.id].showSquadIndicator();
    }

    // Make sure UI camera ignores other players
    if (uiCamera && otherPlayers[p.id].container) {
      uiCamera.ignore(otherPlayers[p.id].container);
    }

    // Show join notification for new players (not reconnections)
    if (!p.isReconnecting && windowManager && p.accountName) {
      windowManager.showNotification(`${p.accountName} joined`, "info");
    }
  };

  // Unified handler for player moved
  const handlePlayerMoved = (id: string, x: number, y: number) => {
    if (id === window.socket.id || !otherPlayers[id]) {
      return;
    }

    const distance = Math.sqrt(
      Math.pow(otherPlayers[id].x - x, 2) +
        Math.pow(otherPlayers[id].y - y, 2),
    );

    const TELEPORT_THRESHOLD = 150;

    if (distance > TELEPORT_THRESHOLD) {
      // Too far, teleport instantly
      otherPlayers[id].setPositionInstant(x, y);
    } else {
      // Normal distance, interpolate smoothly
      otherPlayers[id].setPosition(x, y);
    }
  };

  // Unified handler for player left
  const handlePlayerLeft = (id: string) => {
    if (!otherPlayers[id]) {
      return;
    }

    const otherPlayer = otherPlayers[id];
    
    // Show teleport particle effect at player position before destroying
    if (otherPlayer.x !== undefined && otherPlayer.y !== undefined) {
      showBeamUpParticle(scene, otherPlayer.x, otherPlayer.y);
    }

    otherPlayer.destroy();
    delete otherPlayers[id];
  };

  // Setup listeners based on adapter type
  if (adapterType === "instance") {
    // MainScene: instance-manager events
    window.socket.on(
      "playerJoined",
      (p: {
        id: string;
        x: number;
        y: number;
        weapon?: any;
        activeSkin?: string;
        equippedHat?: number;
        squadCode?: string;
        accountId?: number;
        accountName?: string;
        isReconnecting?: boolean;
      }) => {
        handlePlayerJoined(p);
      },
    );

    window.socket.on(
      "playerState",
      (data: {
        id: string;
        x: number;
        y: number;
        lastProcessedInput?: number;
      }) => {
        // Only handle other players' positions
        if (data.id !== window.socket.id) {
          handlePlayerMoved(data.id, data.x, data.y);
        }
      },
    );

    window.socket.on("playerLeft", (id: string) => {
      handlePlayerLeft(id);
    });
  } else if (adapterType === "market") {
    // MarketScene: market-manager events
    window.socket.on(
      "market-player-entered",
      (data: {
        socketId: string;
        accountId: number;
        accountName: string;
        activeSkin?: string;
        equippedHat?: number;
        weapon?: any;
        position: { x: number; y: number };
      }) => {
        if (data.socketId === window.socket.id) {
          return;
        }

        handlePlayerJoined({
          id: data.socketId,
          x: data.position.x,
          y: data.position.y,
          accountId: data.accountId,
          accountName: data.accountName,
          activeSkin: data.activeSkin,
          equippedHat: data.equippedHat,
          weapon: data.weapon,
        });
      },
    );

    window.socket.on(
      "market-player-moved",
      (data: { socketId: string; position: { x: number; y: number } }) => {
        if (data.socketId === window.socket.id) {
          return;
        }

        handlePlayerMoved(data.socketId, data.position.x, data.position.y);
      },
    );

    window.socket.on("market-player-left", (data: { socketId: string }) => {
      handlePlayerLeft(data.socketId);
    });
  }

  // Handle emoji broadcast - shared between both adapters
  window.socket.on(
    "player-emoji-received",
    (data: { socketId: string; emoji: string }) => {
      if (data.socketId === window.socket.id) {
        // Show above own player
        if (scene.player) {
          scene.player.showEmoji(data.emoji);
        }
      } else if (otherPlayers[data.socketId]) {
        otherPlayers[data.socketId].showEmoji(data.emoji);
      }
    },
  );
}

/**
 * Unregister player listeners.
 */
export function unregisterPlayerListeners(adapterType: "instance" | "market") {
  if (!window.socket) return;

  if (adapterType === "instance") {
    window.socket.off("playerJoined");
    window.socket.off("playerState");
    window.socket.off("playerLeft");
  } else if (adapterType === "market") {
    window.socket.off("market-player-entered");
    window.socket.off("market-player-moved");
    window.socket.off("market-player-left");
  }
  window.socket.off("player-emoji-received");
}
